<template>
  <div class="hcl-wrapper" id="hclWrapper">
    <component :is="componentMeta(definitionRef.meta)" :definition="definitionRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, provide, getCurrentInstance, App, Ref } from 'vue'

import { createPinia } from 'pinia'

import { useDataStore } from '@/stores/data'
import { useStateStore } from '@/stores/state'

import { useMeta } from '@/composables/meta'

import { DefinitionContainer } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  definition?: any
  /**
   * Data as an JS object.
   */
  data?: any
  /**
   * State as an JS object.
   */
  state?: any
  /**
   * Definition as Base64 encoded JSON string.
   */
  defstr?: string
  /**
   * Data as Base64 encoded JSON string.
   */
  datstr?: string
  /**
   * State as Base64 encoded JSON string.
   */
  stastr?: string
}

const props = withDefaults(defineProps<Props>(), {
  definition: {},
  data: {},
  state: {},
  defstr: '',
  datstr: '',
  stastr: ''
})

const definitionRef: Ref<DefinitionContainer> = ref(props.definition)
const dataRef: Ref<Record<string, any>> = ref(props.data)
const stateRef: Ref<Record<string, any>> = ref(props.state)

if (props.defstr.length > 0) definitionRef.value = JSON.parse(atob(props.defstr))
if (props.datstr.length > 0) dataRef.value = JSON.parse(atob(props.datstr))
if (props.stastr.length > 0) stateRef.value = JSON.parse(atob(props.stastr))

provide('wrapperMetaId', definitionRef.value.meta.id ?? '')

const { componentMeta } = useMeta()

const bootstrapPinia = () => {
  const currenInstance: any = getCurrentInstance()

  // pinia handles the Symbol(pinia) in a strange way, so we use
  // the string representation check of the Symbol(pinia) as a fallback
  let piniaInstalled = false
  for (const s of Object.getOwnPropertySymbols(currenInstance?.appContext.provides)) {
    if (s.toString() === 'Symbol(pinia)') {
      piniaInstalled = true
    }
  }

  let pinia
  if (!piniaInstalled) {
    pinia = createPinia()
    pinia.install(currenInstance?.appContext?.app as App)
  }

  return pinia
}

const pinia = bootstrapPinia()

const dataStore = useDataStore(pinia)
const stateStore = useStateStore(pinia)

const { replaceFromBatch: replaceDataFromBatch } = dataStore
replaceDataFromBatch(dataRef.value)

const { updateFromBatch: updateStateFromBatch } = stateStore
updateStateFromBatch(stateRef.value)
</script>

<style lang="scss">
/*
 * Global HCL Styles
 */
@import '@vueform/multiselect/themes/default.css';

.hcl-wrapper {
  .hcl-blocks {
    .row:not(:last-of-type) {
      margin-bottom: 0.5rem;
    }
  }

  .hcl-block {
    .hcl-block-content {
      padding: 0;
      min-height: 2.5rem;
    }
  }

  .multiselect {
    --ms-font-size: 15px;
    --ms-line-height: 1.1;

    .multiselect-placeholder {
      white-space: nowrap;
    }

    &.is-active {
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.1875rem hsla(var(--theme-color-interaction-h), var(--theme-color-interaction-s), var(--theme-color-interaction-l), 0.15);
      border-color: var(--theme-color-interaction);
    }

    .multiselect-option {
      &.is-selected {
        background-color: var(--theme-color-interaction);
      }
    }

    .multiselect-tags {
      .multiselect-tag {
        background-color: var(--theme-color-interaction);
      }
    }

  }
}
</style>
